import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { css, keyframes } from '@emotion/css';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { delay, get } from 'lodash';

const baseHeaderStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const titleTextStyle = css`
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.5rem;
  margin: 0;

  @media (max-width: 380px) {
    margin: 0 0 0.5rem;
  }
`;

const openAnimSide = keyframes`
    from {transform: translateX(100%);}
    to {transform: translateX(0%);}
`;

const closeAnimSide = keyframes`
    from {transform: translateX(0%);}
    to {transform: translateX(100%);}
`;

const openAnimUp = keyframes`
    0% {transform: translateY(100%);}
    100% {transform: translateY(0%);}
`;

const closeAnimDown = keyframes`
    0% {transform: translateY(0%);}
    100% {transform: translateY(100%);}
`;

const closeIconStyle = css`
  cursor: pointer;
  background: white;
  border: none;
  width: fit-content;
`;

const mainContainerStyle = (openPanel: boolean, transitionTime: number, isVisible: boolean) => {
  return css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    z-index: 999;
    max-width: 28rem;
    min-width: 28rem;
    padding: 0;
    box-sizing: border-box;
    border: 1px solid #f1f0ee;
    box-shadow: 0 4px 4px 0 #00000040;
    transform: translateX(100%);
    animation: ${openPanel ? openAnimSide : closeAnimSide} ${transitionTime}s ease both;
    opacity: ${isVisible ? '1' : '0'};
    max-height: 100dvh;

    & .content {
      position: relative;
      height: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    & .panel-content-wrapper,
    & .panel-content {
      height: 100%;
      box-sizing: border-box;
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    @media (max-width: 641px) {
      top: 0;
      right: 0;
      left: 0;
      bottom: -100%;
      min-width: 100%;
      max-width: 100%;
      transform: translateY(100%);
      animation: ${openPanel ? openAnimUp : closeAnimDown} ${transitionTime}s ease forwards;
    }
  `;
};

const Overlay = styled.div`
  display: block;
  position: fixed;
  background: rgba(0, 0, 0, 0);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  overflow: auto;
`;

export type SidePanelContextType = {
  closePanel?: () => void;
};

export const SidePanelContext = createContext<SidePanelContextType | null>(null);

const SidePanel = (props) => {
  const title = get(props, 'title');
  const show = get(props, 'show');
  const onClose = get(props, 'onClose', () => {});
  const onExpand = get(props, 'onExpand', () => {});
  const children = get(props, 'children');
  const headerStyles = get(props, 'headerStyles');
  const containerRef = useRef(null);
  const [openPanel, setOpenPanel] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const transitionTime = 0.2;

  const closePanel = () => {
    setOpenPanel(false);

    delay(() => {
      setIsVisible(false);
      onClose();
    }, transitionTime * 1000);
  };

  const clickHandler = useCallback((evt) => {
    if (!evt.composedPath().includes(get(containerRef, 'current'))) {
      closePanel();
    }
  }, []);

  useEffect(() => {
    delay(() => document.addEventListener('click', clickHandler), 0);

    return () => {
      delay(() => document.removeEventListener('click', clickHandler), 0);
    };
  }, []);

  useEffect(() => {
    if (show) {
      setOpenPanel(true);
      setIsVisible(true);
      onExpand();
    } else {
      setOpenPanel(false);
    }
  }, [show]);

  if (!show) return null;

  const portalElement = document.getElementById('sns-portal') as HTMLElement;

  return ReactDOM.createPortal(
    <SidePanelContext.Provider value={{ closePanel }}>
      <Overlay>
        <div className={mainContainerStyle(openPanel, transitionTime, isVisible)} ref={containerRef}>
          <div className="content">
            <div className={`${baseHeaderStyle} ${headerStyles ? headerStyles : ''}`}>
              <p className={titleTextStyle} role="heading">
                {title}
              </p>
              <button aria-label="Close" data-testid="Close" onClick={closePanel} className={closeIconStyle}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.46967 15.5302C0.762563 15.8231 1.23744 15.8231 1.53033 15.5302L15.5302 1.53033C15.8231 1.23744 15.8231 0.762564 15.5302 0.46967C15.2373 0.176777 14.7625 0.176776 14.4696 0.46967L0.46967 14.4696C0.176777 14.7625 0.176777 15.2373 0.46967 15.5302Z"
                    fill="#2A2928"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.5303 15.5303C15.8232 15.2374 15.8232 14.7626 15.5303 14.4697L1.53045 0.46977C1.23755 0.176876 0.762679 0.176877 0.469786 0.46977C0.176892 0.762663 0.176891 1.23754 0.469786 1.53043L14.4697 15.5303C14.7626 15.8232 15.2375 15.8232 15.5303 15.5303Z"
                    fill="#2A2928"
                  />
                </svg>
              </button>
            </div>

            <div className="panel-content-wrapper">
              <div className="panel-content">{children}</div>
            </div>
          </div>
        </div>
      </Overlay>
    </SidePanelContext.Provider>,
    portalElement,
  );
};

SidePanel.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  onClose: PropTypes.func,
  onExpand: PropTypes.func,
  show: PropTypes.bool,
  children: PropTypes.any,
  headerStyles: PropTypes.string,
};

export default SidePanel;
